import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { FormModel } from "projects/assist/src/app/_shared/form/form-models/form-model";

export class Filter {
  public PageSize: number | null = 10;
  public PageNumber: number = 1;
  public OrderBy: string | null;
  public SortDirection: string | null;
  public OdataFilters: OdataFilter[] = [
    {
      Field: "Id",
      Operator: ComparisonOperator.In,
    },
  ];
  public _version: number | null = null;

  constructor() {
    this.OrderBy = null;
    this.SortDirection = null;
  }

  public getOdataFilter(key: string) {
    return this.OdataFilters.find((f) => f.Field === key);
  }

  disablePaging() {
    //TODO: schould be null
    this.PageNumber = 1;
    this.PageSize = 100000;
  }
}

export class FilterWithIds extends Filter {
  public Ids: number[] | null = null;

  constructor() {
    super();
  }
}

export class FilterFormModel<T extends Filter> extends FormModel<T> {
  odataFilters: OdataFilterFormModel[] = [];
  constructor(
    model: T,
    readonly fg: FormGroup,
    readonly fb: FormBuilder = new FormBuilder()
  ) {
    let odataFilters = model.OdataFilters.map((f) => new OdataFilterFormModel(f));
    let basicFiltersGroup = fb.group({
      PageSize: [model.PageSize ?? 10, Validators.required],
      PageNumber: [model.PageNumber ?? 1, Validators.required],
      OrderBy: [model.OrderBy],
      SortDirection: [model.SortDirection],
      OdataFilters: model.OdataFilters ? fb.array(odataFilters) : fb.array([]),
      _version: [model._version],
    });
    fg.mergeWith(basicFiltersGroup);
    super(model, fg.controls);

    this.odataFilters = odataFilters;
  }

  readonly PageSize = this.get("PageSize") as FormControl;
  readonly PageNumber = this.get("PageNumber") as FormControl;
  readonly OrderBy = this.get("OrderBy") as FormControl;
  readonly SortDirection = this.get("SortDirection") as FormControl;
  readonly OdataFilters = this.get("OdataFilters") as FormArray<OdataFilterFormModel>;

  readonly SearchTerm = this.get("SearchTerm") as FormControl;

  public OdataFilter(name: string): OdataFilterFormModel {
    return (
      this.OdataFilters.controls.find((f) => f.Field.value === name) ||
      new OdataFilterFormModel({ Field: name })
    );
  }

  readonly standardFields = [this.PageSize, this.PageNumber, this.OrderBy, this.SortDirection];

  getPreservedFieldsOnReset(): string[] {
    return ["PageSize", "PageNumber", "OrderBy", "SortDirection", "_version"];
  }

  override mapToModel(): T {
    const copy = new (this.model.constructor as { new (): T })();
    Object.assign(copy, super.mapToModel());
    //model.OdataFilters = this.OdataFilters.controls.map((filter) => filter.mapToModel());
    return copy;
  }

  isDifferentFromStart(): boolean {
    const model = this.mapToModel() as Record<string, unknown>;
    const modelCopy = { ...this.model } as Record<string, unknown>;
    const ignored = [...this.getPreservedFieldsOnReset(), "OdataFilters"];
    for (const field of ignored) {
      delete model[field];
      delete modelCopy[field];
    }
    return JSON.stringify(model) != JSON.stringify(modelCopy);
  }
}

export interface OdataFilter {
  Field: string;
  Operator?: ComparisonOperator;
  Value?: any;
  IsArray?: boolean;
  Property?: string; // This is only for arrays, make this so Field "Person/FirstName" can be replaced by "FirstName" with Property "Person"
  Type?: "string" | "number" | "boolean" | "date" | "datetime";
}

export class OdataFilterFormModel extends FormModel<OdataFilter> {
  constructor(
    model: OdataFilter,
    readonly fb: FormBuilder = new FormBuilder()
  ) {
    super(
      model,
      fb.group({
        Field: [model.Field],
        Operator: [model.Operator],
        Value: [model.Value != undefined ? model.Value : null, { updateOn: "blur" }],
        IsArray: [model.IsArray ? model.IsArray : false],
        Property: [model.Property ? model.Property : null],
        Type: [model.Type ? model.Type : null],
      }).controls
    );
  }

  readonly Field = this.get("Field") as FormControl;
  readonly Operator = this.get("Operator") as FormControl;
  readonly Value = this.get("Value") as FormControl;
  readonly IsArray = this.get("IsArray") as FormControl;
  readonly Property = this.get("Property") as FormControl;
}

//odata filter operators
export enum ComparisonOperator {
  Equal = "eq",
  NotEqual = "ne",
  GreaterThan = "gt",
  GreaterThanOrEqual = "ge",
  LessThan = "lt",
  LessThanOrEqual = "le",
  Contains = "contains",
  ContainsNot = "containsnot",
  In = "in",
  Between = "between",
}
